import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiUploadCloud, FiCalendar } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import "./Checkout.css";

const CURRENCY = "€";

function MedicalConditions() {
  const navigate     = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const shippingData = JSON.parse(localStorage.getItem("shippingData")) || {};

  const [medicalData, setMedicalData] = useState({
    doctorName: "", doctorPhone: "", medications: "",
    treatments: "", allergies: "", dob: "",
    prescription: null, smoke: false, drink: false,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(cart);
  }, []);

  const total      = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const shipping   = 25;
  const grandTotal = total + shipping;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMedicalData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    localStorage.setItem("medicalData", JSON.stringify(medicalData));
    navigate("/checkout/payment");
  };

  return (
    <>
      <Helmet>
        <title>Medical Conditions | Checkout</title>
        <meta name="description" content="Enter your medical details and continue secure checkout." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://kamagrastar.eu/medical-conditions" />
      </Helmet>

      <div className="checkout-page">

        {/* STEPS */}
        <div className="checkout-steps">
          <div className="step done" onClick={() => navigate("/checkout")}>
            <span>✓</span><p>Shipping</p>
          </div>
          <div className="line active"></div>
          <div className="step active"><span>2</span><p>Medical Conditions</p></div>
          <div className="line"></div>
          <div className="step"><span>3</span><p>Payments</p></div>
        </div>

        <div className="checkout-wrapper">

          {/* LEFT */}
          <div className="checkout-left">
            <h2>Medical Conditions</h2>

            <form onSubmit={handleSubmit}>

              <div className="grid-2">
                <input type="text" name="doctorName" placeholder="Primary Physician's Name"
                  value={medicalData.doctorName} onChange={handleChange} />
                <input type="text" name="doctorPhone" placeholder="Physician's Contact Number"
                  value={medicalData.doctorPhone} onChange={handleChange} />
              </div>

              <div className="grid-2">
                <input type="text" name="medications" placeholder="Current Medications"
                  value={medicalData.medications} onChange={handleChange} />
                <input type="text" name="treatments" placeholder="Current Treatments"
                  value={medicalData.treatments} onChange={handleChange} />
              </div>

              <input type="text" name="allergies" placeholder="Drug Allergies"
                value={medicalData.allergies} onChange={handleChange} />

              <div className="upload-row">
                <div className="prescription-box">
                  <label className="file-upload-btn">
                    <input type="file" hidden accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setMedicalData(prev => ({
                        ...prev, prescription: e.target.files[0]
                      }))} />
                    <FiUploadCloud className="upload-icon" />
                    <span>{medicalData.prescription ? medicalData.prescription.name : "Choose Files"}</span>
                  </label>
                  <div className="upload-text">
                    <p>Upload your prescription</p>
                    <small>Max file 3 MB (jpg, png & pdf)</small>
                  </div>
                </div>

                <label className="dob-box">
                  <FiCalendar className="calendar-icon" />
                  <input type="date" name="dob" value={medicalData.dob} onChange={handleChange} />
                  {!medicalData.dob && <span>Your Date of Birth</span>}
                </label>
              </div>

              <p className="fax-text">
                Alternatively, you can send your prescription via Fax:
                <strong> +44 7471 300 624 </strong>
              </p>

              <div className="habit-box">
                <h3>Lifestyle Habits</h3>
                <p className="habit-subtitle">Select a relevant option</p>
                <div className="habit-options">
                  <label className="habit-check">
                    <input type="checkbox" name="smoke"
                      checked={medicalData.smoke} onChange={handleChange} />
                    <span>Smoke</span>
                  </label>
                  <label className="habit-check">
                    <input type="checkbox" name="drink"
                      checked={medicalData.drink} onChange={handleChange} />
                    <span>Drink</span>
                  </label>
                </div>
                <p className="habit-note">
                  I certify that I am over 18, under a licensed doctor's care,
                  and the medication is solely for my personal use, strictly not for resale.
                </p>
                <h4 className="habit-agree">
                  By pressing 'Proceed', I accept all the statements mentioned above.
                </h4>
              </div>

              <button type="submit" className="continue-btn">Proceed to payment</button>

            </form>
          </div>

          {/* RIGHT */}
          <div className="checkout-right">

            <div className="summary-card">
              <h3>Order Summary ({cartItems.length} items)</h3>
              {cartItems.map((item, index) => (
                <div className="summary-item" key={index}>
                  <img src={item.image} alt={item.productName} loading="lazy" />
                  <div className="summary-info">
                    <h4>{item.productName}</h4>
                    <p>{item.qty} x {item.packSize}</p>
                  </div>
                  <span>{CURRENCY}{(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
              <div className="summary-total">
                <div><span>Subtotal</span><span>{CURRENCY}{total.toFixed(2)}</span></div>
                <div><span>International Shipping</span><span>{CURRENCY}{shipping.toFixed(2)}</span></div>
                <div className="grand-total">
                  <span>Order Total</span>
                  <span>{CURRENCY}{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="shipping-card">
              <div className="shipping-top">
                <h4>Shipping Address</h4>
                <span className="change-link" onClick={() => navigate("/checkout")}>Change</span>
              </div>
              <p>{shippingData.firstName} {shippingData.lastName}</p>
              <p>{shippingData.address}</p>
              {shippingData.apartment && <p>{shippingData.apartment}</p>}
              <p>{shippingData.city}, {shippingData.state} {shippingData.zip}</p>
              <p>{shippingData.country}</p>
              <p className="phone-text">{shippingData.phone}</p>
            </div>

            <div className="delivery-note">
              <p>We try to deliver your order within 10 days, but in certain cases,
                delivery may take up to 20-30 working days.</p>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default MedicalConditions;